import { Container, Graphics, Sprite, type ColorSource } from 'pixi.js';
import type { IParticleEmitter, ParticleEmitterConfig } from './IRenderer';
import { PixiOverlayHost } from './PixiOverlayHost';
import { getSharedTexture } from './PixiSprite';
import { Z_LAYERS } from './Z_LAYERS';
import { AllocCounters } from '../diagnostics/AllocCounters';

/** Lerp two 0xRRGGBB colors in RGB space (t: 0→1). Exported for tests. */
export function lerpColor(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 0xff, ag = (a >> 8) & 0xff, ab = a & 0xff;
  const br = (b >> 16) & 0xff, bg = (b >> 8) & 0xff, bb = b & 0xff;
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bl = Math.round(ab + (bb - ab) * t);
  return (r << 16) | (g << 8) | bl;
}

/**
 * PixiParticleEmitter — IParticleEmitter impl. Custom mini-emitter that
 * runs on Pixi Graphics + manual integration, driven by PixiOverlayHost
 * tickListeners. Each particle is one PIXI.Graphics circle; per-frame
 * step lerps alpha/scale and integrates velocity + gravity.
 *
 * Why not @pixi/particle-emitter v5?
 * Our 7 emitters share a small parameter set (speed, scale lerp, alpha
 * lerp, gravity, lifespan, blend, color) — no advanced behaviors that
 * the lib brings (path follow, color list, ordered z, rotation animate).
 * A custom mini-emitter is ~150 lines, uses only primitives we already
 * own, and avoids a Pixi v8 / particle-emitter v5 compat layer.
 *
 * Phase H-B Stage 5l.
 */

/** A particle's display object — a tinted circle OR a tinted glow sprite. */
type ParticleNode = Graphics | Sprite;

interface ActiveParticle {
  node: ParticleNode;
  x: number;
  y: number;
  vx: number;
  vy: number;
  ageMs: number;
  lifeMs: number;
  scaleStart: number;
  scaleEnd: number;
  alphaStart: number;
  alphaEnd: number;
}

export class PixiParticleEmitter implements IParticleEmitter {
  private static ACTIVE = new Set<PixiParticleEmitter>();

  private container: Container | null = null;
  private particles: ActiveParticle[] = [];
  /** Free-list of dead nodes (hidden, removed from container) for reuse —
   *  kills the per-particle GC churn that capped affordable density. */
  private pool: ParticleNode[] = [];
  /** Whether textured (glow-sprite) particles are in use this emitter. */
  private useTexture = false;
  private destroyed = false;
  private continuousActive = false;
  private continuousAccumMs = 0;
  private posX = 0;
  private posY = 0;
  private unsubTick: (() => void) | null = null;

  constructor(private cfg: ParticleEmitterConfig) {
    void PixiOverlayHost.ensureReady().then(() => {
      if (this.destroyed) return;
      this.build();
    });
  }

  private build(): void {
    // Textured particles need the shared glow texture registered; if it's
    // not ready at build time we fall back to circles (acquireNode re-checks).
    this.useTexture = !!this.cfg.textureKey;
    const container = new Container();
    // Default to MOBS layer (zIndex 10) for backward compatibility —
    // particles historically sat at this depth in the world container.
    container.zIndex = this.cfg.depth ?? Z_LAYERS.MOBS;
    if (this.cfg.screenSpace) {
      PixiOverlayHost.addUiChild(container);
    } else {
      PixiOverlayHost.addWorldChild(container);
    }
    this.container = container;
    PixiParticleEmitter.ACTIVE.add(this);

    this.unsubTick = PixiOverlayHost.addTickListener((dtMs) => {
      this.tick(dtMs);
    });
  }

  private tick(dtMs: number): void {
    AllocCounters.inc('emitter.tick.entry');
    if (!this.container) return;
    AllocCounters.inc('emitter.tick.particlesIterated', this.particles.length);

    // Continuous spawn cadence
    if (this.continuousActive && this.cfg.mode === 'continuous') {
      const freq = this.cfg.frequencyMs ?? 100;
      this.continuousAccumMs += dtMs;
      while (this.continuousAccumMs >= freq) {
        this.continuousAccumMs -= freq;
        this.spawnOne(this.posX, this.posY);
      }
    }

    // Integrate active particles
    const dtSec = dtMs / 1000;
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.ageMs += dtMs;

      if (p.ageMs >= p.lifeMs) {
        this.release(p.node);
        this.particles.splice(i, 1);
        continue;
      }

      // Position integration
      p.x += p.vx * dtSec;
      p.y += p.vy * dtSec;
      p.vy += (this.cfg.gravityY ?? 0) * dtSec;

      // Animate alpha + scale (+ color over life when colorEnd set)
      const t = p.ageMs / p.lifeMs;
      const alpha = p.alphaStart + (p.alphaEnd - p.alphaStart) * t;
      const scale = p.scaleStart + (p.scaleEnd - p.scaleStart) * t;

      p.node.x = p.x;
      p.node.y = p.y;
      p.node.alpha = alpha;
      p.node.scale.set(scale, scale);
      if (this.cfg.colorEnd !== undefined) {
        p.node.tint = lerpColor(this.cfg.color, this.cfg.colorEnd, t) as ColorSource;
      }
    }
  }

  /**
   * Hide + park a dead node for reuse (no destroy → no GC). Hidden by alpha,
   * not `visible`: flipping `visible` is a structural change that makes Pixi
   * rebuild the whole stage's instruction set, and particles die and respawn
   * dozens of times a second (plan 13 §12). An alpha-0 quad draws nothing.
   */
  private release(node: ParticleNode): void {
    node.alpha = 0;
    this.pool.push(node);
  }

  /** A fresh or recycled node, configured for this emitter's look. */
  private acquireNode(): ParticleNode {
    const recycled = this.pool.pop();
    if (recycled) return recycled; // spawnOne sets its alpha

    const radius = this.cfg.particleRadius ?? 4;
    const tex = this.useTexture ? getSharedTexture(this.cfg.textureKey!) : undefined;
    if (tex) {
      const s = new Sprite(tex);
      s.anchor.set(0.5);
      s.width = radius * 2;
      s.height = radius * 2;
      s.tint = this.cfg.color as ColorSource;
      if (this.cfg.blendMode && this.cfg.blendMode !== 'normal') s.blendMode = this.cfg.blendMode;
      return s;
    }
    const g = new Graphics();
    // White fill + tint so color-over-life can lerp via .tint.
    g.circle(0, 0, radius).fill({ color: 0xffffff, alpha: 1 });
    g.tint = this.cfg.color as ColorSource;
    if (this.cfg.blendMode && this.cfg.blendMode !== 'normal') g.blendMode = this.cfg.blendMode;
    return g;
  }

  private spawnOne(x: number, y: number): void {
    if (!this.container) return;
    AllocCounters.inc('emitter.spawnOne');

    const sx = this.cfg.spawnXMin !== undefined && this.cfg.spawnXMax !== undefined
      ? this.cfg.spawnXMin + Math.random() * (this.cfg.spawnXMax - this.cfg.spawnXMin)
      : x;
    const sy = this.cfg.spawnYMin !== undefined && this.cfg.spawnYMax !== undefined
      ? this.cfg.spawnYMin + Math.random() * (this.cfg.spawnYMax - this.cfg.spawnYMin)
      : y;

    const speedMin = this.cfg.speedMin;
    const speedMax = this.cfg.speedMax;
    const speed = speedMin + Math.random() * (speedMax - speedMin);
    // Phaser angles: 0° = right, sweeps clockwise for positive y-down screen.
    const angleDeg = this.cfg.angleMinDeg + Math.random() * (this.cfg.angleMaxDeg - this.cfg.angleMinDeg);
    const angleRad = angleDeg * Math.PI / 180;
    const vx = Math.cos(angleRad) * speed;
    const vy = Math.sin(angleRad) * speed;

    const node = this.acquireNode();
    node.x = sx;
    node.y = sy;
    node.alpha = this.cfg.alphaStart;
    node.scale.set(this.cfg.scaleStart, this.cfg.scaleStart);
    if (this.cfg.colorEnd !== undefined) node.tint = this.cfg.color as ColorSource;
    if (node.parent !== this.container) this.container.addChild(node);

    this.particles.push({
      node,
      x: sx, y: sy, vx, vy,
      ageMs: 0,
      lifeMs: this.cfg.lifespanMs,
      scaleStart: this.cfg.scaleStart,
      scaleEnd: this.cfg.scaleEnd,
      alphaStart: this.cfg.alphaStart,
      alphaEnd: this.cfg.alphaEnd,
    });
  }

  setPosition(x: number, y: number): void {
    this.posX = x;
    this.posY = y;
  }

  explode(count: number, x: number, y: number): void {
    if (!this.container) {
      // Build pending — defer the burst.
      void PixiOverlayHost.ensureReady().then(() => {
        if (!this.destroyed) this.explode(count, x, y);
      });
      return;
    }
    for (let i = 0; i < count; i++) this.spawnOne(x, y);
  }

  start(): void {
    this.continuousActive = true;
    this.continuousAccumMs = 0;
  }

  stop(): void {
    this.continuousActive = false;
  }

  destroy(): void {
    this.destroyed = true;
    PixiParticleEmitter.ACTIVE.delete(this);
    this.unsubTick?.();
    this.unsubTick = null;
    for (const p of this.particles) p.node.destroy();
    for (const n of this.pool) n.destroy();
    this.particles = [];
    this.pool = [];
    if (this.container) {
      this.container.destroy({ children: true });
      this.container = null;
    }
  }

  /** Total live particles across all active emitters. */
  static getTotalParticles(): number {
    let total = 0;
    for (const em of PixiParticleEmitter.ACTIVE) {
      total += em.particles.length;
    }
    return total;
  }

  /** Number of active emitter instances. */
  static getActiveEmitterCount(): number {
    return PixiParticleEmitter.ACTIVE.size;
  }
}
